// ============================================
// Opportunities List Page
// ============================================

import { useState, useMemo, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useSearch } from '@tanstack/react-router';
import { toast } from 'sonner';
import { AppLayout } from '@/components/AppLayout';
import { TenantBadge } from '@/components/TenantBadge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select } from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { api } from '@/lib/api';
import { DataTableSkeleton, KanbanSkeleton } from '@/components/skeletons';
import { EmptyState } from '@/components/ui/empty-state';
import { Skeleton } from '@/components/ui/skeleton';
import { useTenant } from '@/hooks/useTenant';
import { buildTenantDisplayMap, getTenantDisplayById } from '@/lib/tenant-display';
import { OPPORTUNITY_STAGE, type OpportunityStage } from '@muraka/shared';
import {
  Plus,
  Search,
  Loader2,
  TrendingUp,
  LayoutGrid,
  List,
  MoreVertical,
  ChevronLeft,
  ChevronRight,
  Calendar,
} from 'lucide-react';

// Stage configuration with labels and colors
const stageConfig: Record<
  OpportunityStage,
  { label: string; colorClass: string; dotClass: string; probability: number }
> = {
  qualification: {
    label: 'Qualification',
    colorClass: 'bg-slate-100 text-slate-700',
    dotClass: 'bg-slate-500',
    probability: 10,
  },
  needs_analysis: {
    label: 'Needs Analysis',
    colorClass: 'bg-blue-100 text-blue-700',
    dotClass: 'bg-blue-500',
    probability: 25,
  },
  proposal: {
    label: 'Proposal',
    colorClass: 'bg-purple-100 text-purple-700',
    dotClass: 'bg-purple-500',
    probability: 50,
  },
  negotiation: {
    label: 'Negotiation',
    colorClass: 'bg-orange-100 text-orange-700',
    dotClass: 'bg-orange-500',
    probability: 75,
  },
  closed_won: {
    label: 'Closed Won',
    colorClass: 'bg-green-100 text-green-700',
    dotClass: 'bg-green-500',
    probability: 100,
  },
  closed_lost: {
    label: 'Closed Lost',
    colorClass: 'bg-red-100 text-red-700',
    dotClass: 'bg-red-500',
    probability: 0,
  },
};

// Open stages (for filtering and kanban)
const openStages: OpportunityStage[] = [
  OPPORTUNITY_STAGE.QUALIFICATION,
  OPPORTUNITY_STAGE.NEEDS_ANALYSIS,
  OPPORTUNITY_STAGE.PROPOSAL,
  OPPORTUNITY_STAGE.NEGOTIATION,
];

// All stages for kanban
const allStages: OpportunityStage[] = [
  OPPORTUNITY_STAGE.QUALIFICATION,
  OPPORTUNITY_STAGE.NEEDS_ANALYSIS,
  OPPORTUNITY_STAGE.PROPOSAL,
  OPPORTUNITY_STAGE.NEGOTIATION,
  OPPORTUNITY_STAGE.CLOSED_WON,
  OPPORTUNITY_STAGE.CLOSED_LOST,
];

// Avatar colors
const formatCurrency = (amount: number) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  }).format(amount);
};

const formatDate = (date: string) => {
  return new Date(date).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
};

const formatShortDate = (date: string) => {
  return new Date(date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

const getDaysLeft = (closeDate: string, stage: string) => {
  if (stage === 'closed_won') return 'Won';
  if (stage === 'closed_lost') return 'Lost';
  const days = Math.ceil(
    (new Date(closeDate).getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24)
  );
  if (days < 0) return `${Math.abs(days)} days overdue`;
  if (days === 0) return 'Due today';
  return `${days} days left`;
};

const getDaysLeftClass = (closeDate: string) => {
  const days = Math.ceil(
    (new Date(closeDate).getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24)
  );
  if (days < 0) return 'text-destructive';
  if (days <= 7) return 'text-amber-600';
  return 'text-muted-foreground';
};

const getProbabilityBarColor = (prob: number) => {
  if (prob >= 75) return 'bg-green-500';
  if (prob >= 50) return 'bg-purple-500';
  if (prob >= 25) return 'bg-blue-500';
  return 'bg-slate-500';
};

const getDefaultCloseDate = () => {
  return new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
};

export function OpportunitiesPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const searchParams = useSearch({ from: '/opportunities' });
  const { isTenantContextReady, activeTenantId, mode, tenantList } = useTenant();

  // State
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [stageFilter, setStageFilter] = useState<OpportunityStage | ''>('');
  const [viewMode, setViewMode] = useState<'list' | 'kanban'>('list');
  const [mobileStageIndex, setMobileStageIndex] = useState(0);
  const [createModalOpen, setCreateModalOpen] = useState(() => searchParams.create === 'true');
  const [openDropdownId, setOpenDropdownId] = useState<string | null>(null);

  // Clear search param from URL after modal opens
  useEffect(() => {
    if (searchParams.create === 'true') {
      navigate({ to: '/opportunities', search: {}, replace: true });
    }
  }, [searchParams.create, navigate]);

  // Form state
  const [formData, setFormData] = useState(() => ({
    name: '',
    accountId: '',
    contactId: '',
    stage: OPPORTUNITY_STAGE.QUALIFICATION as OpportunityStage,
    amount: 0,
    expectedCloseDate: getDefaultCloseDate(),
    notes: '',
  }));

  // Query accounts for dropdown
  const { data: accountsData } = useQuery({
    queryKey: ['accounts-list', mode, activeTenantId],
    queryFn: () => api.getAccounts({ pageSize: 100 }),
    enabled: isTenantContextReady,
  });

  // Query opportunities list
  const { data, isLoading, error } = useQuery({
    queryKey: ['opportunities', mode, activeTenantId, page, search, stageFilter],
    queryFn: () =>
      api.getOpportunities({
        page,
        pageSize: 50,
        search: search || undefined,
        stage: stageFilter || undefined,
      }),
    enabled: isTenantContextReady,
  });

  // Query opportunities kanban
  const { data: kanbanData, isLoading: isKanbanLoading } = useQuery({
    queryKey: ['opportunities-kanban', mode, activeTenantId],
    queryFn: () => api.getOpportunitiesKanban(),
    enabled: isTenantContextReady && viewMode === 'kanban',
  });

  // Create mutation
  const createMutation = useMutation({
    mutationFn: api.createOpportunity.bind(api),
    onSuccess: () => {
      toast.success('Opportunity created successfully');
      queryClient.invalidateQueries({ queryKey: ['opportunities'] });
      queryClient.invalidateQueries({ queryKey: ['opportunities-kanban'] });
      setCreateModalOpen(false);
      resetForm();
    },
    onError: error => {
      toast.error(error instanceof Error ? error.message : 'Failed to create opportunity');
    },
  });

  const resetForm = () => {
    setFormData({
      name: '',
      accountId: '',
      contactId: '',
      stage: OPPORTUNITY_STAGE.QUALIFICATION,
      amount: 0,
      expectedCloseDate: getDefaultCloseDate(),
      notes: '',
    });
  };

  const handleCreate = (e: React.FormEvent) => {
    e.preventDefault();
    // Convert date to ISO datetime string (schema expects datetime format)
    const expectedCloseDateISO = formData.expectedCloseDate.includes('T')
      ? formData.expectedCloseDate
      : `${formData.expectedCloseDate}T00:00:00.000Z`;

    createMutation.mutate({
      name: formData.name,
      accountId: formData.accountId,
      contactId: formData.contactId || null,
      stage: formData.stage,
      amount: formData.amount,
      expectedCloseDate: expectedCloseDateISO,
      notes: formData.notes || null,
    });
  };

  const pagination = data?.pagination;

  // Define opportunity type for type safety
  type OpportunityData = {
    id: string;
    name: string;
    stage: OpportunityStage;
    amount: number;
    expectedCloseDate?: string;
    accountName?: string;
    notes?: string;
    isStale?: boolean;
    tenantId: string;
  };

  // Memoize opportunities to avoid creating new array reference on each render
  const opportunities = useMemo(() => (data?.data || []) as OpportunityData[], [data?.data]);
  const accounts = useMemo(
    () => (accountsData?.data || []) as { id: string; name: string }[],
    [accountsData?.data]
  );
  const showTenantColumn = mode === 'all';
  const tenantDisplayMap = useMemo(() => buildTenantDisplayMap(tenantList), [tenantList]);

  // Calculate metrics
  const metrics = useMemo(() => {
    const openOpps = opportunities.filter(
      (o: OpportunityData) => o.stage !== 'closed_won' && o.stage !== 'closed_lost'
    );
    const totalPipeline = openOpps.reduce(
      (sum: number, o: OpportunityData) => sum + (o.amount || 0),
      0
    );
    const weightedPipeline = openOpps.reduce((sum: number, o: OpportunityData) => {
      const prob = stageConfig[o.stage as OpportunityStage]?.probability || 0;
      return sum + (o.amount || 0) * (prob / 100);
    }, 0);
    const currentMonth = new Date().toISOString().slice(0, 7);
    const closingThisMonth = openOpps.filter((o: OpportunityData) =>
      o.expectedCloseDate?.startsWith(currentMonth)
    ).length;
    return {
      totalPipeline,
      weightedPipeline,
      openDeals: openOpps.length,
      closingThisMonth,
    };
  }, [opportunities]);

  // Get stage counts for pills
  const stageCounts = useMemo(() => {
    const counts: Record<string, number> = { all: opportunities.length };
    openStages.forEach(stage => {
      counts[stage] = opportunities.filter((o: OpportunityData) => o.stage === stage).length;
    });
    return counts;
  }, [opportunities]);

  // Get opportunities by stage for kanban
  const getOpportunitiesByStage = (stage: OpportunityStage): OpportunityData[] => {
    if (viewMode === 'kanban' && kanbanData) {
      return (kanbanData[stage] || []) as OpportunityData[];
    }
    return opportunities.filter((o: OpportunityData) => o.stage === stage);
  };

  // Mobile stage navigation
  const prevMobileStage = () => {
    if (mobileStageIndex > 0) setMobileStageIndex(i => i - 1);
  };
  const nextMobileStage = () => {
    if (mobileStageIndex < allStages.length - 1) setMobileStageIndex(i => i + 1);
  };

  return (
    <AppLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-foreground">Opportunities</h1>
            <p className="text-muted-foreground mt-1">Manage your sales pipeline and deals</p>
          </div>
          <div className="flex items-center gap-3">
            {/* View Toggle (Desktop) */}
            <div className="hidden lg:flex items-center bg-muted rounded-lg p-1">
              <button
                onClick={() => setViewMode('list')}
                className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                  viewMode === 'list'
                    ? 'bg-card text-foreground shadow-sm'
                    : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                <List className="h-4 w-4" />
              </button>
              <button
                onClick={() => setViewMode('kanban')}
                className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                  viewMode === 'kanban'
                    ? 'bg-card text-foreground shadow-sm'
                    : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                <LayoutGrid className="h-4 w-4" />
              </button>
            </div>
            <Button onClick={() => setCreateModalOpen(true)} data-testid="new-opportunity-button">
              <Plus className="h-4 w-4" />
              New Opportunity
            </Button>
          </div>
        </div>

        {/* Pipeline Summary Cards */}
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <div className="bg-card rounded-xl border border-border p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                <TrendingUp className="h-5 w-5 text-primary" />
              </div>
              <div>
                <p className="text-2xl font-bold text-foreground">
                  {formatCurrency(metrics.totalPipeline)}
                </p>
                <p className="text-sm text-muted-foreground">Total Pipeline</p>
              </div>
            </div>
          </div>
          <div className="bg-card rounded-xl border border-border p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-purple-100 flex items-center justify-center">
                <svg
                  className="h-5 w-5 text-purple-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
                  />
                </svg>
              </div>
              <div>
                <p className="text-2xl font-bold text-foreground">
                  {formatCurrency(metrics.weightedPipeline)}
                </p>
                <p className="text-sm text-muted-foreground">Weighted Value</p>
              </div>
            </div>
          </div>
          <div className="bg-card rounded-xl border border-border p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center">
                <svg
                  className="h-5 w-5 text-green-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
              </div>
              <div>
                <p className="text-2xl font-bold text-foreground">{metrics.openDeals}</p>
                <p className="text-sm text-muted-foreground">Open Deals</p>
              </div>
            </div>
          </div>
          <div className="bg-card rounded-xl border border-border p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center">
                <svg
                  className="h-5 w-5 text-amber-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
              </div>
              <div>
                <p className="text-2xl font-bold text-foreground">{metrics.closingThisMonth}</p>
                <p className="text-sm text-muted-foreground">Closing This Month</p>
              </div>
            </div>
          </div>
        </div>

        {/* List View (Desktop) */}
        {viewMode === 'list' && (
          <>
            {/* Filters */}
            <div className="hidden lg:block bg-card rounded-xl border border-border">
              <div className="p-4 flex flex-col sm:flex-row gap-4">
                <div className="flex-1 relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Search opportunities..."
                    className="pl-10"
                    value={search}
                    onChange={e => {
                      setSearch(e.target.value);
                      setPage(1);
                    }}
                  />
                </div>
                <div className="flex gap-2">
                  <Select
                    value={stageFilter}
                    onChange={e => {
                      setStageFilter(e.target.value as OpportunityStage | '');
                      setPage(1);
                    }}
                  >
                    <option value="">All Stages</option>
                    {Object.entries(stageConfig).map(([value, config]) => (
                      <option key={value} value={value}>
                        {config.label}
                      </option>
                    ))}
                  </Select>
                </div>
              </div>

              {/* Stage Pills */}
              <div className="px-4 pb-4 flex flex-wrap gap-2">
                <button
                  onClick={() => {
                    setStageFilter('');
                    setPage(1);
                  }}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
                    stageFilter === ''
                      ? 'bg-primary text-primary-foreground'
                      : 'bg-muted text-muted-foreground hover:bg-muted/80'
                  }`}
                >
                  All ({stageCounts.all})
                </button>
                {openStages.map(stage => (
                  <button
                    key={stage}
                    onClick={() => {
                      setStageFilter(stage);
                      setPage(1);
                    }}
                    className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
                      stageFilter === stage
                        ? 'bg-primary text-primary-foreground'
                        : 'bg-muted text-muted-foreground hover:bg-muted/80'
                    }`}
                  >
                    {stageConfig[stage].label} ({stageCounts[stage] || 0})
                  </button>
                ))}
              </div>
            </div>

            {/* Table (Desktop) */}
            <div className="hidden lg:block bg-card rounded-xl border border-border overflow-hidden">
              {isLoading ? (
                <div className="p-4">
                  <DataTableSkeleton columnCount={showTenantColumn ? 8 : 7} rowCount={8} />
                </div>
              ) : error ? (
                <div className="flex flex-col items-center justify-center py-12 text-center">
                  <p className="text-destructive mb-2">Failed to load opportunities</p>
                </div>
              ) : opportunities.length === 0 ? (
                search || stageFilter ? (
                  <EmptyState
                    title="No opportunities found"
                    description="Try adjusting your search or filters"
                    actionLabel="Clear Query"
                    onAction={() => {
                      setSearch('');
                      setStageFilter('');
                      setPage(1);
                    }}
                    icon={Search}
                  />
                ) : (
                  <EmptyState
                    title="No opportunities yet"
                    description="Create opportunities to track your potential deals."
                    actionLabel="Create Opportunity"
                    onAction={() => setCreateModalOpen(true)}
                    icon={TrendingUp}
                  />
                )
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow className="bg-muted/30">
                      <TableHead className="text-xs font-medium text-muted-foreground uppercase tracking-wider px-5">
                        Opportunity
                      </TableHead>
                      <TableHead className="text-xs font-medium text-muted-foreground uppercase tracking-wider px-5">
                        Account
                      </TableHead>
                      <TableHead className="text-xs font-medium text-muted-foreground uppercase tracking-wider px-5">
                        Stage
                      </TableHead>
                      <TableHead className="text-xs font-medium text-muted-foreground uppercase tracking-wider px-5">
                        Amount
                      </TableHead>
                      <TableHead className="text-xs font-medium text-muted-foreground uppercase tracking-wider px-5">
                        Probability
                      </TableHead>
                      <TableHead className="text-xs font-medium text-muted-foreground uppercase tracking-wider px-5">
                        Close Date
                      </TableHead>
                      {showTenantColumn && (
                        <TableHead className="w-[180px] text-xs font-medium text-muted-foreground uppercase tracking-wider px-5">
                          Tenant
                        </TableHead>
                      )}
                      <TableHead className="w-12"></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {opportunities.map((opp: OpportunityData) => {
                      const prob = stageConfig[opp.stage as OpportunityStage]?.probability || 0;
                      const weighted = opp.amount * (prob / 100);
                      const tenantDisplay = getTenantDisplayById(opp.tenantId, tenantDisplayMap);
                      return (
                        <TableRow
                          key={opp.id}
                          role="button"
                          tabIndex={0}
                          className={`cursor-pointer hover:bg-muted/50 transition-colors focus-visible:outline-none focus-visible:bg-muted/50 ${
                            opp.isStale
                              ? 'bg-amber-50/50'
                              : opp.stage === 'closed_won'
                                ? 'bg-green-50/50'
                                : ''
                          }`}
                          onClick={() =>
                            navigate({ to: '/opportunities/$id', params: { id: opp.id } })
                          }
                          onKeyDown={e => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              navigate({ to: '/opportunities/$id', params: { id: opp.id } });
                            }
                          }}
                        >
                          <TableCell className="px-5 py-4">
                            <div className="flex items-center gap-2">
                              <div>
                                <p className="font-medium text-foreground">{opp.name}</p>
                                {opp.notes && (
                                  <p className="text-sm text-muted-foreground truncate max-w-[200px]">
                                    {opp.notes}
                                  </p>
                                )}
                              </div>
                              {opp.isStale && (
                                <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-amber-100 text-amber-700">
                                  Stale
                                </span>
                              )}
                            </div>
                          </TableCell>
                          <TableCell className="px-5 py-4 text-sm text-foreground">
                            {opp.accountName || 'No Account'}
                          </TableCell>
                          <TableCell className="px-5 py-4">
                            <span
                              className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${stageConfig[opp.stage as OpportunityStage]?.colorClass}`}
                            >
                              {stageConfig[opp.stage as OpportunityStage]?.label}
                            </span>
                          </TableCell>
                          <TableCell className="px-5 py-4">
                            <p className="text-sm font-medium text-foreground">
                              {formatCurrency(opp.amount)}
                            </p>
                            <p className="text-xs text-muted-foreground">
                              {formatCurrency(weighted)} weighted
                            </p>
                          </TableCell>
                          <TableCell className="px-5 py-4">
                            <div className="flex items-center gap-2">
                              <div className="w-16 h-1.5 bg-muted rounded-full overflow-hidden">
                                <div
                                  className={`h-full rounded-full ${getProbabilityBarColor(prob)}`}
                                  style={{ width: `${prob}%` }}
                                />
                              </div>
                              <span className="text-sm text-foreground">{prob}%</span>
                            </div>
                          </TableCell>
                          <TableCell className="px-5 py-4">
                            <p className="text-sm text-foreground">
                              {opp.expectedCloseDate ? formatDate(opp.expectedCloseDate) : '—'}
                            </p>
                            <p
                              className={`text-xs ${opp.expectedCloseDate ? getDaysLeftClass(opp.expectedCloseDate) : ''}`}
                            >
                              {opp.expectedCloseDate
                                ? getDaysLeft(opp.expectedCloseDate, opp.stage)
                                : '—'}
                            </p>
                          </TableCell>
                          {showTenantColumn && (
                            <TableCell className="px-5 py-4">
                              <TenantBadge
                                tenantName={tenantDisplay.name}
                                brandColor={tenantDisplay.brandColor}
                                density="compact"
                              />
                            </TableCell>
                          )}
                          <TableCell className="px-5 py-4">
                            <button
                              onClick={e => {
                                e.stopPropagation();
                                setOpenDropdownId(openDropdownId === opp.id ? null : opp.id);
                              }}
                              className="p-1.5 rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted"
                            >
                              <MoreVertical className="h-4 w-4" />
                            </button>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              )}

              {/* Pagination */}
              {pagination && pagination.totalPages > 1 && (
                <div className="px-5 py-4 border-t border-border flex items-center justify-between">
                  <p className="text-sm text-muted-foreground">
                    Showing{' '}
                    <span className="font-medium text-foreground">
                      {(page - 1) * pagination.pageSize + 1}
                    </span>{' '}
                    to{' '}
                    <span className="font-medium text-foreground">
                      {Math.min(page * pagination.pageSize, pagination.total)}
                    </span>{' '}
                    of <span className="font-medium text-foreground">{pagination.total}</span>{' '}
                    opportunities
                  </p>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage(p => p - 1)}
                      disabled={page === 1}
                    >
                      Previous
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage(p => p + 1)}
                      disabled={page === pagination.totalPages}
                    >
                      Next
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </>
        )}

        {/* Kanban View (Desktop) */}
        {viewMode === 'kanban' && (
          <div className="hidden lg:block">
            {isKanbanLoading ? (
              <KanbanSkeleton />
            ) : (
              <div className="flex gap-4 pb-4 overflow-x-auto">
                {allStages.map(stage => {
                  const stageOpps = getOpportunitiesByStage(stage);
                  const totalValue = stageOpps.reduce((sum: number, o) => sum + (o.amount || 0), 0);
                  const config = stageConfig[stage];

                  return (
                    <div key={stage} className="w-72 shrink-0 flex flex-col rounded-xl bg-muted/50">
                      <div className="p-3 border-b border-border/50">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <div className={`w-3 h-3 rounded-full ${config.dotClass}`} />
                            <h3 className="font-medium text-sm text-foreground">{config.label}</h3>
                            <span className="text-xs text-muted-foreground bg-muted px-1.5 py-0.5 rounded">
                              {stageOpps.length}
                            </span>
                          </div>
                        </div>
                        <div className="flex justify-between text-xs text-muted-foreground mt-2">
                          <span>{formatCurrency(totalValue)}</span>
                          <span>{config.probability}%</span>
                        </div>
                      </div>
                      <div className="flex-1 p-2 space-y-2 overflow-y-auto max-h-[calc(100vh-400px)]">
                        {stageOpps.length === 0 ? (
                          <p className="text-sm text-muted-foreground text-center py-4">
                            No opportunities
                          </p>
                        ) : (
                          stageOpps.map(opp => {
                            const tenantDisplay = getTenantDisplayById(
                              opp.tenantId,
                              tenantDisplayMap
                            );

                            return (
                              <div
                                key={opp.id}
                                role="button"
                                tabIndex={0}
                                onClick={() =>
                                  navigate({ to: '/opportunities/$id', params: { id: opp.id } })
                                }
                                onKeyDown={e => {
                                  if (e.key === 'Enter' || e.key === ' ') {
                                    e.preventDefault();
                                    navigate({ to: '/opportunities/$id', params: { id: opp.id } });
                                  }
                                }}
                                className={`bg-card rounded-lg border p-3 hover:border-primary/30 transition-colors cursor-pointer shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${
                                  opp.isStale
                                    ? 'border-amber-200 bg-amber-50/50'
                                    : stage === 'closed_won'
                                      ? 'border-green-200'
                                      : stage === 'closed_lost'
                                        ? 'border-red-200 opacity-75'
                                        : 'border-border'
                                }`}
                              >
                                {showTenantColumn && (
                                  <div className="mb-2">
                                    <TenantBadge
                                      tenantName={tenantDisplay.name}
                                      brandColor={tenantDisplay.brandColor}
                                      className="max-w-[12rem]"
                                    />
                                  </div>
                                )}
                                <div className="flex items-start justify-between">
                                  <p className="font-medium text-sm text-foreground">{opp.name}</p>
                                  {opp.isStale && (
                                    <span className="inline-flex items-center px-1 py-0.5 rounded text-[9px] font-medium bg-amber-100 text-amber-700">
                                      Stale
                                    </span>
                                  )}
                                </div>
                                <p className="text-xs text-muted-foreground mt-1">
                                  {opp.accountName || 'No Account'}
                                </p>
                                <div className="flex items-center justify-between mt-3">
                                  <span
                                    className={`text-sm font-semibold ${
                                      stage === 'closed_won'
                                        ? 'text-green-600'
                                        : stage === 'closed_lost'
                                          ? 'text-red-600 line-through'
                                          : 'text-foreground'
                                    }`}
                                  >
                                    {formatCurrency(opp.amount)}
                                  </span>
                                  {!['closed_won', 'closed_lost'].includes(stage) &&
                                    opp.expectedCloseDate && (
                                      <div className="flex items-center gap-1">
                                        <Calendar
                                          className={`h-3.5 w-3.5 ${
                                            getDaysLeftClass(opp.expectedCloseDate).includes(
                                              'amber'
                                            )
                                              ? 'text-amber-600'
                                              : 'text-muted-foreground'
                                          }`}
                                        />
                                        <span
                                          className={`text-xs ${
                                            getDaysLeftClass(opp.expectedCloseDate).includes(
                                              'amber'
                                            )
                                              ? 'text-amber-600'
                                              : 'text-muted-foreground'
                                          }`}
                                        >
                                          {formatShortDate(opp.expectedCloseDate)}
                                        </span>
                                      </div>
                                    )}
                                  {stage === 'closed_won' && (
                                    <span className="text-xs text-green-600">Won</span>
                                  )}
                                  {stage === 'closed_lost' && (
                                    <span className="text-xs text-red-600">Lost</span>
                                  )}
                                </div>
                              </div>
                            );
                          })
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* Mobile View */}
        <div className="lg:hidden">
          {isLoading ? (
            <div className="p-4 space-y-4">
              {Array.from({ length: 3 }).map((_, i) => (
                <Skeleton key={i} className="h-40 w-full rounded-xl" />
              ))}
            </div>
          ) : opportunities.length === 0 ? (
            search || stageFilter ? (
              <EmptyState
                title="No opportunities found"
                description="Try adjusting your search or filters"
                actionLabel="Clear Query"
                onAction={() => {
                  setSearch('');
                  setStageFilter('');
                  setPage(1);
                }}
                icon={Search}
              />
            ) : (
              <EmptyState
                title="No opportunities yet"
                description="Create opportunities to track your potential deals."
                actionLabel="Create Opportunity"
                onAction={() => setCreateModalOpen(true)}
                icon={TrendingUp}
              />
            )
          ) : (
            <>
              {/* Mobile Stage Selector */}
              <div className="flex items-center justify-between mb-4 bg-card rounded-xl border border-border p-3">
                <button
                  onClick={prevMobileStage}
                  disabled={mobileStageIndex === 0}
                  className="p-2 text-muted-foreground hover:text-foreground disabled:opacity-50"
                >
                  <ChevronLeft className="h-5 w-5" />
                </button>
                <div className="text-center">
                  <div className="flex items-center justify-center gap-2">
                    <div
                      className={`w-3 h-3 rounded-full ${stageConfig[allStages[mobileStageIndex]].dotClass}`}
                    />
                    <span className="font-medium text-foreground">
                      {stageConfig[allStages[mobileStageIndex]].label}
                    </span>
                    <span className="text-xs text-muted-foreground bg-muted px-1.5 py-0.5 rounded">
                      {getOpportunitiesByStage(allStages[mobileStageIndex]).length}
                    </span>
                  </div>
                  <p className="text-sm text-muted-foreground mt-1">
                    {formatCurrency(
                      getOpportunitiesByStage(allStages[mobileStageIndex]).reduce(
                        (sum: number, o) => sum + (o.amount || 0),
                        0
                      )
                    )}{' '}
                    · {stageConfig[allStages[mobileStageIndex]].probability}%
                  </p>
                </div>
                <button
                  onClick={nextMobileStage}
                  disabled={mobileStageIndex === allStages.length - 1}
                  className="p-2 text-muted-foreground hover:text-foreground disabled:opacity-50"
                >
                  <ChevronRight className="h-5 w-5" />
                </button>
              </div>

              {/* Stage Dots */}
              <div className="flex justify-center gap-1.5 mb-4">
                {allStages.map((stage, index) => (
                  <button
                    key={stage}
                    onClick={() => setMobileStageIndex(index)}
                    className={`w-2 h-2 rounded-full ${
                      mobileStageIndex === index ? 'bg-primary' : 'bg-muted'
                    }`}
                  />
                ))}
              </div>

              {/* Mobile Cards */}
              <div className="space-y-3">
                {getOpportunitiesByStage(allStages[mobileStageIndex]).length === 0 ? (
                  <div className="p-8 text-center text-muted-foreground bg-card rounded-xl border border-border">
                    <p>No opportunities in this stage</p>
                  </div>
                ) : (
                  getOpportunitiesByStage(allStages[mobileStageIndex]).map(opp => {
                    const prob = stageConfig[opp.stage as OpportunityStage]?.probability || 0;
                    const tenantDisplay = getTenantDisplayById(opp.tenantId, tenantDisplayMap);
                    return (
                      <div
                        key={opp.id}
                        onClick={() =>
                          navigate({ to: '/opportunities/$id', params: { id: opp.id } })
                        }
                        className="bg-card rounded-xl border border-border p-4 hover:border-primary/30 transition-colors cursor-pointer"
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-foreground">{opp.name}</p>
                            <p className="text-sm text-muted-foreground mt-0.5">
                              {opp.accountName || 'No Account'}
                            </p>
                          </div>
                          <span
                            className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium shrink-0 ml-2 ${stageConfig[opp.stage as OpportunityStage]?.colorClass}`}
                          >
                            {stageConfig[opp.stage as OpportunityStage]?.label}
                          </span>
                        </div>
                        <div className="grid grid-cols-3 gap-4 mt-4">
                          <div>
                            <p className="text-lg font-semibold text-foreground">
                              {formatCurrency(opp.amount)}
                            </p>
                            <p className="text-xs text-muted-foreground">Amount</p>
                          </div>
                          <div>
                            <p className="text-lg font-semibold text-foreground">{prob}%</p>
                            <p className="text-xs text-muted-foreground">Probability</p>
                          </div>
                          <div>
                            <p className="text-lg font-semibold text-foreground">
                              {opp.expectedCloseDate ? formatShortDate(opp.expectedCloseDate) : '—'}
                            </p>
                            <p className="text-xs text-muted-foreground">Close Date</p>
                          </div>
                        </div>
                        {showTenantColumn && (
                          <div className="mt-3">
                            <TenantBadge
                              tenantName={tenantDisplay.name}
                              brandColor={tenantDisplay.brandColor}
                              className="max-w-[12rem]"
                            />
                          </div>
                        )}
                        <div className="flex items-center justify-between mt-3 pt-3 border-t border-border">
                          <span className="text-sm text-muted-foreground">
                            {opp.expectedCloseDate
                              ? getDaysLeft(opp.expectedCloseDate, opp.stage)
                              : '—'}
                          </span>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </>
          )}
        </div>
      </div>

      {/* Create Opportunity Modal */}
      <Dialog open={createModalOpen} onClose={() => setCreateModalOpen(false)}>
        <DialogContent onClose={() => setCreateModalOpen(false)} className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>New Opportunity</DialogTitle>
            <DialogDescription>Add a new opportunity to your pipeline</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleCreate}>
            <div className="p-6 pt-0 space-y-6 max-h-[calc(100vh-200px)] overflow-y-auto">
              {/* Basic Information Section */}
              <div>
                <h3 className="text-sm font-medium text-foreground mb-4 flex items-center gap-2">
                  <svg
                    className="w-4 h-4 text-muted-foreground"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                  Basic Information
                </h3>
                <div className="grid gap-4 sm:grid-cols-2">
                  <div className="sm:col-span-2">
                    <Input
                      label="Opportunity Name"
                      required
                      placeholder="e.g., Acme Corp - Enterprise License"
                      value={formData.name}
                      onChange={e => setFormData({ ...formData, name: e.target.value })}
                      data-testid="opportunity-name-input"
                    />
                  </div>
                  <Input
                    label="Amount ($)"
                    type="number"
                    min="0"
                    placeholder="0"
                    value={formData.amount || ''}
                    onChange={e => setFormData({ ...formData, amount: Number(e.target.value) })}
                    data-testid="opportunity-amount-input"
                  />
                  <Input
                    label="Expected Close Date"
                    type="date"
                    required
                    value={formData.expectedCloseDate}
                    onChange={e => setFormData({ ...formData, expectedCloseDate: e.target.value })}
                    data-testid="opportunity-closedate-input"
                  />
                  <Select
                    label="Stage"
                    value={formData.stage}
                    onChange={e =>
                      setFormData({ ...formData, stage: e.target.value as OpportunityStage })
                    }
                    data-testid="opportunity-stage-select"
                  >
                    {Object.entries(stageConfig).map(([value, config]) => (
                      <option key={value} value={value}>
                        {config.label}
                      </option>
                    ))}
                  </Select>
                </div>
              </div>

              {/* Related Records Section */}
              <div>
                <h3 className="text-sm font-medium text-foreground mb-4 flex items-center gap-2">
                  <svg
                    className="w-4 h-4 text-muted-foreground"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1"
                    />
                  </svg>
                  Related Records
                </h3>
                <div className="grid gap-4 sm:grid-cols-2">
                  <Select
                    label="Account"
                    required
                    value={formData.accountId}
                    onChange={e => setFormData({ ...formData, accountId: e.target.value })}
                    data-testid="opportunity-account-select"
                  >
                    <option value="">Select Account</option>
                    {accounts.map((account: { id: string; name: string }) => (
                      <option key={account.id} value={account.id}>
                        {account.name}
                      </option>
                    ))}
                  </Select>
                </div>
              </div>

              {/* Additional Information Section */}
              <div>
                <h3 className="text-sm font-medium text-foreground mb-4 flex items-center gap-2">
                  <svg
                    className="w-4 h-4 text-muted-foreground"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                    />
                  </svg>
                  Additional Information
                </h3>
                <Textarea
                  label="Notes"
                  rows={3}
                  placeholder="Describe this opportunity..."
                  value={formData.notes}
                  onChange={e => setFormData({ ...formData, notes: e.target.value })}
                  data-testid="opportunity-notes-input"
                />
              </div>
            </div>
            <DialogFooter className="bg-muted/30">
              <Button
                type="button"
                variant="outline"
                onClick={() => setCreateModalOpen(false)}
                data-testid="create-opportunity-cancel"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={createMutation.isPending}
                data-testid="create-opportunity-submit"
              >
                {createMutation.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
                Create Opportunity
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </AppLayout>
  );
}
